import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { Section } from "../components/SectionTitle";
import { Button } from "../components/ui/button";
import SEO from "../components/SEO";
import { BLOG_CATEGORIES } from "../lib/constants";
import { ArrowRight, Calendar, Tag } from "lucide-react";

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  } catch { return ""; }
}

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [cat, setCat] = useState("All");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get("/blog/posts", { params: cat !== "All" ? { category: cat } : {} })
      .then((r) => { if (!cancelled) setPosts(r.data); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [cat]);

  return (
    <div>
      <SEO
        title="Insights & Resources Blog"
        description="Articles, guides, and practical tips on financial planning, tax preparation, insurance protection, estate planning, and business services from HSA Service Hub."
        keywords="financial planning blog, tax tips, estate planning support, family protection planning, business services blog"
        path="/blog"
      />
      <section className="bg-[#0a2540] text-white py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 max-w-4xl">
          <div className="eyebrow text-[#d4af37] mb-3">Insights & Resources</div>
          <h1 className="font-serif-hsa text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] mb-5">
            Helpful Resources for Financial, Tax, Insurance & Legal Planning
          </h1>
          <p className="text-lg text-white/85 max-w-2xl">
            Articles, guides, and practical tips from the HSA Service Hub editorial team.
          </p>
        </div>
      </section>

      <Section className="bg-white">
        <div className="flex flex-wrap gap-2 mb-10">
          {BLOG_CATEGORIES.map((c) => (
            <button
              key={c}
              data-testid={`blog-filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
              onClick={() => setCat(c)}
              className={`px-4 py-2 text-sm font-medium rounded-full border transition-colors ${
                cat === c
                  ? "bg-[#0a2540] text-white border-[#0a2540]"
                  : "bg-white text-slate-700 border-[#cbd5e1] hover:border-[#1e6dd8] hover:text-[#1e6dd8]"
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="text-center py-20 text-slate-500">Loading articles...</div>
        ) : posts.length === 0 ? (
          <div className="text-center py-20 text-slate-500">No articles in this category yet.</div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {posts.map((p) => (
              <Link
                key={p.id}
                to={`/blog/${p.slug}`}
                data-testid={`blog-card-${p.slug}`}
                className="group lift-card bg-white border border-[#cbd5e1] rounded-2xl overflow-hidden flex flex-col"
              >
                {p.cover_image && (
                  <div className="aspect-[16/10] overflow-hidden">
                    <img src={p.cover_image} alt={p.title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  </div>
                )}
                <div className="p-6 flex-1 flex flex-col">
                  <div className="flex items-center gap-3 text-xs text-slate-500 mb-3">
                    <span className="inline-flex items-center gap-1.5"><Tag className="w-3.5 h-3.5 text-[#1e6dd8]" />{p.category}</span>
                    <span className="inline-flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" />{formatDate(p.created_at)}</span>
                  </div>
                  <h3 className="font-serif-hsa text-xl font-semibold text-[#0a2540] leading-snug mb-2 group-hover:text-[#1e6dd8] transition-colors">
                    {p.title}
                  </h3>
                  <p className="text-sm text-slate-600 leading-relaxed line-clamp-3 flex-1">{p.excerpt}</p>
                  <div className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-[#0a2540] group-hover:text-[#1e6dd8]">
                    Read article <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </Section>
    </div>
  );
}
